import React, {useRef, useCallback, useMemo, useContext} from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { makeStyles } from '@mui/styles';
import SMCField from '../SMCField';
import SMCCheckboxContext from './SMCCheckboxContext';
import {hasValue} from '../../../utils/utils';
import {useSMCDisabledContext} from '../SMCDisabledContext';

/**
 * 
 * @param {object} params
 * @param {boolean} [params.disabled]
 * @param {boolean} [params.ignoreDisabledContext]
 * @param {function} [params.onChange]
 * @param {boolean} [params.labelClickable]
 * @param {(string|number)} [params.height]
 * @param {(string|number)} [params.width]
 * @param {boolean} [params.checked]
 * @param {string} [params.label]
 * @param {('start'|'end')} [params.labelPosition]
 * @param {(string|number)} [params.labelWidth]
 * @param {string} [params.tooltip]
 * @param {boolean} [params.inline]
 * @param {*} [params.innerRef]
 * @param {string} [params.className]
 */
function SMCCheckbox({
    disabled,
    ignoreDisabledContext,
    onChange,
    style,
    labelClickable=true,
    height,
    checked,
    label,
    width,
    labelPosition,
    labelWidth,
    tooltip,
    inline,
    innerRef,
    className,
    value
}) {
    const classes = useStyles();
    const checkboxGroupContext = useContext(SMCCheckboxContext);
    const disabledContext = useSMCDisabledContext();

    const ref = useRef();
    const checkboxRef = innerRef || ref;

    const checkboxChecked = useMemo(() => {
        if (hasValue(checked)) {
            return checked;
        }
        else if (checkboxGroupContext && Array.isArray(checkboxGroupContext.checkedValues)) {
            return checkboxGroupContext.checkedValues.includes(value);
        }
        else {
            return undefined;
        }
    }, [checkboxGroupContext, checked, value]);

    const checkboxOnChange = useCallback((e) => {
        onChange && onChange(e);
        
        if (checkboxGroupContext && hasValue(checkboxGroupContext.setCheckedValues) && hasValue(value)) {
            const checked = e.target.checked;
            const checkedValues = checkboxGroupContext.checkedValues;
            if (checked) {
                checkboxGroupContext.setCheckedValues([...checkedValues, value]);
            }
            else {
                checkboxGroupContext.setCheckedValues(checkedValues.filter(checkedValue => checkedValue !== value));
            }
        }
    }, [checkboxGroupContext, onChange, value]);
    
    const checkboxDisabled = useMemo(() => {
        if (disabledContext && !ignoreDisabledContext) {
            if (disabledContext.forceDisable) {
                return true;
            }
            if (disabledContext.forceEnable) {
                return false;
            }
        }
        if (checkboxGroupContext && hasValue(checkboxGroupContext.disabled)) {
            return checkboxGroupContext.disabled;
        }
        if (hasValue(disabled)) {
            return disabled;
        }
        return undefined;
    }, [checkboxGroupContext, disabled, disabledContext, ignoreDisabledContext]);

    const onLabelClick = (e) => {
        if (labelClickable) {
            e.target = checkboxRef.current;
            e.currentTarget = checkboxRef.current;
            e.target.checked = !checkboxRef.current.checked;
            checkboxOnChange(e);
        }
    }

    const checkbox = (
        <input
            type="checkbox"
            style={style}
            checked={checkboxChecked}
            onChange={checkboxOnChange}
            className={clsx(className, classes.root)}
            disabled={checkboxDisabled}
            ref={checkboxRef}
        />
    )

    return (
        <SMCField
            disabled={checkboxDisabled}
            height={height}
            inline={inline}
            // Unless explicitly stated that label position is start, place it at end
            labelPosition={labelPosition === 'start' ? 'start' : 'end'}
            labelWidth={labelWidth}
            tooltip={tooltip}
            width={width}
            label={label}
            labelOnClick={labelClickable ? onLabelClick : undefined}
        >
            { checkbox }
        </SMCField>
    )
}

const useStyles = makeStyles({    
    root: {
        position: 'relative',
        cursor: 'pointer',
        marginLeft: '0px',
        marginRight: '7px',
        '&:disabled': {
            cursor: 'default'
        },
        '&::before': {
            height: '12px',
            width: '12px',
            backgroundColor: '#ffffff',
            content: '""',
            display: 'inline-block',
            verticalAlign: 'baseline',
            transition: '0.3s',
            border: '1px solid #D2D5D9',
        },
        '&:hover:before': {
            border: '1px solid #4FB4F1'
        },
        '&:active:before': {
            backgroundColor: '#cccdcf',
            border: '1px solid #000'
        },
        '&:disabled:before': {
            border: '1px solid #E7E9EB'
        },
        /* The checkmark*/
        '&:checked:after': {
            content: '""',
            display: 'inline-block',
            width: '3px',
            height: '6px',
            border: 'solid black',
            borderWidth: '0 2px 2px 0',
            transform: 'rotate(45deg)',
            position: 'absolute',
            top: '2px',
            left: '5px'
        },
        /* Disabled checkmark*/
        '&:checked:disabled:after': {
            border: 'solid #a7a9ac',
            borderWidth: '0 2px 2px 0'
        }
    }
})

SMCCheckbox.propTypes = {
    disabled: PropTypes.bool,
    onChange: PropTypes.func,
    labelClickable: PropTypes.bool,
    height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    checked: PropTypes.bool,
    label: PropTypes.node,
    tooltip: PropTypes.node,
    labelPosition: PropTypes.oneOf(['start', 'end']),
    labelWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    inline: PropTypes.bool,
    innerRef: PropTypes.node,
    className: PropTypes.string
}

// SMCCheckbox.whyDidYouRender = true;

export default React.memo(SMCCheckbox);